import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, XCircle, Loader2 } from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { getCheckoutStatus, getPaypalStatus, getPaypalOrderStatus } from '../lib/api';

const TIDS = {
  successPage: 'payment-success-page',
  cancelPage: 'payment-cancel-page',
  statusIcon: 'payment-status-icon',
  statusTitle: 'payment-status-title',
  statusDesc: 'payment-status-desc',
  homeCta: 'payment-home-cta',
};

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <header className="px-6 sm:px-12 py-5 border-b border-black/5">
      <Link to="/" className="inline-flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0071E3] text-white">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
        </span>
        <span className="text-[15px] font-semibold tracking-tight text-[#1D1D1F]">
          Premium Laundry Services
        </span>
      </Link>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 py-16">
      {children}
    </main>
  </div>
);

export const PaymentSuccess = () => {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', data: null, error: 'Missing session id' });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setState({ status: 'paid', data, error: null });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'expired', data, error: null });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: 'timeout', data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setState({ status: 'error', data: null, error: e?.message });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return renderResult(state, t, TIDS.successPage);
};

export const PaypalSuccess = () => {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const subscriptionId = params.get('subscription_id');
  const orderId = subscriptionId ? null : params.get('order_id') || params.get('token');
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    if (!subscriptionId && !orderId) {
      setState({ status: 'error', data: null, error: 'Missing reference id' });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = subscriptionId
          ? await getPaypalStatus(subscriptionId)
          : await getPaypalOrderStatus(orderId);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setState({ status: 'paid', data, error: null });
          return;
        }
        if (['CANCELLED', 'EXPIRED', 'VOIDED'].includes(data.status)) {
          setState({ status: 'expired', data, error: null });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: 'timeout', data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setState({ status: 'error', data: null, error: e?.message });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId, orderId]);

  return renderResult(state, t, 'paypal-success-page');
};

const renderResult = (state, t, testId) => {
  const isLoading = state.status === 'loading';
  const isPaid = state.status === 'paid';
  const isFailed = state.status === 'expired' || state.status === 'error' || state.status === 'timeout';

  return (
    <Shell>
      <motion.div
        data-testid={testId}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md"
      >
        <div
          data-testid={TIDS.statusIcon}
          className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
            isPaid ? 'bg-[#0071E3]/10' : isFailed ? 'bg-red-100' : 'bg-[#F5F5F7]'
          }`}
        >
          {isLoading && <Loader2 className="w-7 h-7 text-[#0071E3] animate-spin" />}
          {isPaid && <Check className="w-8 h-8 text-[#0071E3]" strokeWidth={3} />}
          {isFailed && <XCircle className="w-8 h-8 text-red-500" />}
        </div>

        <h1
          data-testid={TIDS.statusTitle}
          className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1D1D1F]"
        >
          {isPaid
            ? t.payment.successTitle
            : isFailed
              ? t.payment.failedTitle
              : t.payment.processing}
        </h1>

        <p
          data-testid={TIDS.statusDesc}
          className="mt-4 text-[15px] text-[#86868B] leading-relaxed"
        >
          {isPaid
            ? t.payment.successDesc
            : isFailed
              ? t.payment.failedDesc
              : ''}
        </p>

        {(isPaid || isFailed) && (
          <Link
            data-testid={TIDS.homeCta}
            to="/"
            className="inline-block mt-8 bg-[#0071E3] hover:bg-[#0077ED] text-white font-semibold rounded-full px-8 py-3 transition-transform hover:scale-[1.03] active:scale-95"
          >
            {t.payment.successCta}
          </Link>
        )}
      </motion.div>
    </Shell>
  );
};

export const PaymentCancel = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <Shell>
      <motion.div
        data-testid={TIDS.cancelPage}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md"
      >
        <div
          data-testid={TIDS.statusIcon}
          className="mx-auto w-16 h-16 rounded-full bg-[#F5F5F7] flex items-center justify-center mb-6"
        >
          <XCircle className="w-8 h-8 text-[#86868B]" />
        </div>
        <h1
          data-testid={TIDS.statusTitle}
          className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1D1D1F]"
        >
          {t.payment.cancelTitle}
        </h1>
        <p
          data-testid={TIDS.statusDesc}
          className="mt-4 text-[15px] text-[#86868B] leading-relaxed"
        >
          {t.payment.cancelDesc}
        </p>
        <button
          data-testid={TIDS.homeCta}
          onClick={() => navigate('/')}
          className="mt-8 bg-[#0071E3] hover:bg-[#0077ED] text-white font-semibold rounded-full px-8 py-3 transition-transform hover:scale-[1.03] active:scale-95"
        >
          {t.payment.cancelCta}
        </button>
      </motion.div>
    </Shell>
  );
};
